define((require) => {
    "use strict";

    const $                     = require("jquery");
    const {throttle, contains}  = require("underscore");
    const EditorEvents          = require("shared/services/EditorEvents");
    const EventDispatcher       = require("shared/util/EventDispatcher");
    const Keycode               = require("shared/services/KeyCode");
    const WidgetValidator       = require("./WidgetValidator");

    const COPY_PASTE_MODIFIER_KEY_CODES = [Keycode.CONTROL, Keycode.LEFT_COMMAND_MACOS_WEBKIT, Keycode.META_OR_COMMAND_MACOS_FIREFOX, Keycode.CONTEXT_MENU_OR_RIGHT_COMMAND_MACOS_WEBKIT];
    let modifierDown = false;

    /**
     * The clipboard singleton.
     *
     * <p>This singleton takes care of detecting the copy and paste shortcuts, triggering an event when either is
     * detected.</p>
     *
     * <p>When a listener receives a "copy" event, it can set the Clipboard data by calling setData. If there are more
     * than one copy listener registered, they are responsible for determining which listener should set the data. For
     * example, in the editor view, a widget is copied if selected, otherwise the scene is copied. The copy event
     * includes one argument, the metadata object, which contains one property, isInput, boolean for whether the
     * copy occurred in an input field.</p>
     *
     * <p>The "paste" event includes two arguments: the clipboard data, and metadata for the paste. The metadata
     * includes pastesSinceLastCopy, the number of pastes that have already occurred since the last copy (not including
     * the current paste), isInput, boolean for whether the paste occurred in an input field, and position, the optional
     * position for the paste. The position may not be available, since it only really applies to paste via mouse
     * right-click. If it is specified, it will be an object containing "top" and "left" properties (pixels relative to
     * the body).</p>
     *
     * @class Clipboard
     * @constructor
     */
    const Clipboard = EventDispatcher.extend({

        /**
         * The data currently kept in the clipboard.
         * @type {Object}
         */
        _data: null,

        _pasteCounts: {},

        constructor() {
            // Create a throttled version of #paste - as the editor sometimes have
            // a hard time keeping up with multiple paste events (user holds down CTRL+V)
            // for certain widgets (e.g. text)
            this.paste = throttle(this.paste.bind(this), 250);

            // Listen for clipboard shortcut keys
            $(document)
                .on("keydown", this._keyDownListener.bind(this))
                .on("keyup", this._keyUpListener.bind(this));
        },

        _keyDownListener(event) {
            if (contains(COPY_PASTE_MODIFIER_KEY_CODES, event.keyCode)) {
                modifierDown = true;
            }

            if (!modifierDown) {
                return;
            }

            switch (event.keyCode) {
                case Keycode.C:
                    this.copy();
                    break;

                case Keycode.V:
                    this.paste();
                    break;
                default: // do nothing
            }
        },

        _keyUpListener(event) {
            if (contains(COPY_PASTE_MODIFIER_KEY_CODES, event.keyCode)) {
                modifierDown = false;
            }
        },

        /**
         * Check if the current active element is input, textarea or contenteditable
         */
        isInputActive() {
            const $ele = $(document.activeElement);
            return $ele.is("input, textarea") || $ele.attr("contenteditable") === "true";
        },

        /**
         * Copies the currently-selected data to the clipboard, via an event to the clipboard's clients.
         */
        copy() {
            if (this.isInputActive() || window.getSelection().type === "Range") {
                return;
            }

            this._pasteCounts = {};
            // This should make someone call #setData
            this.trigger("copy");

            // This sends events to Mix Panel.
            this._triggerEditorEvent(EditorEvents.constants.COPY_WIDGET);
        },

        /**
         * Pastes the current clipboard data, via an event to the clipboard's clients.
         */
        paste(position) {
            let isWidgetsValid = true;

            if (this._data && !this.isInputActive()) {
                if (this._data.isEmailModeEnabled && this._data.models) {
                    isWidgetsValid =
                        !WidgetValidator.getActiveIncompatibleEmailWidgets({
                            widgetModelsToCheck: this._data.models,
                            systemMessage: `You are trying to paste widgets that cannot be used in emails: .`
                        }).length;
                }

                if (isWidgetsValid) {
                    this.trigger("paste", this._data, {
                        pasteCounts: this._pasteCounts,
                        position
                    });
                    // Signal that a paste action occurred
                    this._triggerEditorEvent(EditorEvents.constants.PASTE_WIDGET);
                }
            }
        },

        /**
         * Sets the current clipboard data.
         * @param {Object} data The clipboard data.
         */
        setData(data) {
            this._data = data;
            this._pasteCounts[data.sceneId] = 1;
        },

        getData() {
            return this._data;
        },

        /**
         * Data type constants.
         */
        DataTypes: {
            WIDGET: "widget",
            SCENE: "scene"
        },

        /**
         * Sends Mix Panel events for clipboard actions.
         * @param {String} eventName The name of the event, either a copy or a paste action.
         * @private
         */
        _triggerEditorEvent(eventName) {
            if (this._data) {
                const eventProperties = {
                    targetType: this._data.type,
                    triggerOrigin: "SceneEditor"
                };

                if (eventProperties.targetType === "widget") {
                    const widgetTypes = this._data.models
                        ? this._data.models.map((widget) => widget.get("type"))
                        : [];
                    if (widgetTypes.length > 0) {
                        eventProperties.widgetTypes = widgetTypes.join();
                        eventProperties.numberOfWidgetSelected = widgetTypes.length;
                    }
                }

                if (this._data.experience) {
                    eventProperties.experienceId = this._data.experience.get("id");
                    eventProperties.experienceVersion = this._data.experience.get("version");
                    eventProperties.experiencePushedVersion = this._data.experience.get("pushedVersion");
                }

                EditorEvents.trigger(eventName, eventProperties);
            }
        }
    });

    return new Clipboard();
});



// WEBPACK FOOTER //
// ./src/editor/services/Clipboard.js